'use client';

import { useRouter } from 'next/navigation';
import { useSession } from 'next-auth/react';
import { useTranslations } from 'next-intl';
import { use } from 'react';
import { LocalTimeDisplay } from '@/app/(auth-routes)/(blocked-by-onboarding)/inbox/[[...match_id]]/_components/LocalTimeDisplay';
import { trpc } from '@/app/_trpc/client';
import { AccountType } from '@/autogenerated/@prisma/prisma-mongo-client';
import { HelpPreference } from '@/autogenerated/@prisma/prisma-mongo-client';
import { AccountActionsDialog } from '@/components/features/admin/AccountActionsDialog';
import { DialogContext } from '@/components/features/dialogs/infra/context/DialogContext';
import HelpPreferenceTags from '@/components/features/HelpPreferenceTags';
import { LanguageProficiencyDisplay } from '@/components/features/LanguageProficiencyDisplay';
import { ShareProfileButton } from '@/components/features/ShareProfileButton';
import { ToHelpButton } from '@/components/features/ToHelpButton';
import AvailabilityViewer from '@/components/ui/availability/AvailabilityViewer';
import Avatar from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import Icon, { IconName } from '@/components/ui/Icon';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { TruncateText } from '@/components/ui/TruncateText';
import { supportedCountries } from '@/constants/countries';
import { iconDimensions } from '@/constants/familyStructure';
import { FamilyStructure } from '@/constants/familyStructure';
import { TimeSlot, UserDefaultTimezone } from '@/constants/timeSlots';
import { useIsMobile } from '@/hooks/useIsMobile';
import { cn } from '@/lib/utils';
import { ProfileCardUser } from '../types';

export default function ProfileCard({
  loggedInUser,
  withoutLayout = false,
  user,
  onClose,
  currentUserId,
  hideActions = false,
  showSkeletonOnEmptyState = false,
}: {
  loggedInUser: boolean;
  withoutLayout?: boolean;
  user: Partial<ProfileCardUser> & { id: string };
  onClose?: () => void;
  currentUserId?: string;
  hideActions?: boolean;
  showSkeletonOnEmptyState?: boolean;
}) {
  const router = useRouter();
  const t = useTranslations('Profile');
  const tNationality = useTranslations('nationality');
  const tFamilyStructure = useTranslations('FamilyStructureRadioGroup');
  const { data: session } = useSession();
  const { openDialog, closeDialog } = use(DialogContext);
  const [isMobile] = useIsMobile();
  const adminView = session?.user?.admin;

  const thisIsCurrentUserProfile = user.id === currentUserId;
  const { data: matches, isLoading: isLoadingMatches } =
    trpc.matches.getMatchIds.useQuery(undefined, {
      enabled: loggedInUser && !thisIsCurrentUserProfile,
    });

  const matchId = matches?.find((match) => match.userId === user.id)?.matchId;

  const shouldShowProfile =
    thisIsCurrentUserProfile ||
    user.publicProfile === true ||
    adminView ||
    (loggedInUser && matchId);

  if (!shouldShowProfile && !isLoadingMatches) {
    router.push('/not-found');
    return null;
  }

  const showSurprisingCountryWarning =
    user.accountType === AccountType.Refugee &&
    !supportedCountries.includes(user.nationality || '') &&
    !user.eligibilityVerified &&
    adminView;

  if (user.familyStructure === null) {
    user.familyStructure = FamilyStructure.SINGLE;
  }

  // Fetch current user's profile to get their timezone
  const { data: currentUserProfile, isLoading: isLoadingCurrentUserProfile } =
    trpc.users.getById.useQuery(
      { id: currentUserId || '' },
      { enabled: !!currentUserId }
    );
  const currentUserTimezone =
    currentUserProfile?.profile?.timezone ||
    (typeof window !== 'undefined'
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : UserDefaultTimezone);

  const CareerLine = ({
    industry,
    jobTitle,
    title,
    type,
  }: {
    industry?: string;
    jobTitle?: string | null;
    title: string;
    type: 'past' | 'current' | 'future';
  }) => {
    if (!industry && !jobTitle && !showSkeletonOnEmptyState) {
      return null;
    }

    const formattedJobTitle = jobTitle
      ? jobTitle.charAt(0).toUpperCase() + jobTitle.slice(1).toLowerCase()
      : type === 'past'
        ? t('worked')
        : type === 'current'
          ? t('working')
          : t('wantToWork');

    const formattedIndustry = industry
      ? industry.charAt(0).toUpperCase() + industry.slice(1).toLowerCase()
      : '';

    const workPhrase = formattedIndustry
      ? t('workPhrase', {
          jobTitle: formattedJobTitle,
          industry: formattedIndustry,
        })
      : formattedJobTitle;

    return (
      <div className='flex flex-col gap-2'>
        <h3 className='text-xs font-bold'>{title}</h3>
        {!industry && !jobTitle && showSkeletonOnEmptyState ? (
          <Skeleton className='h-4 w-1/2' />
        ) : (
          <p className='text-sm'>
            <span>{workPhrase}</span>
          </p>
        )}
      </div>
    );
  };
  const ReportButton = () => {
    if (thisIsCurrentUserProfile) {
      return null;
    }

    return (
      <Button
        variant={isMobile ? 'link' : 'tertiary'}
        size='medium'
        className='text-destructive md:text-foreground flex w-fit gap-2'
        onClick={() => {
          if (!session) {
            closeDialog('profile');
            openDialog('signup', {
              userName: user.name?.split(' ')[0] || 'User',
            });
            return;
          }
          closeDialog('profile');
          openDialog('report-user', {
            user: {
              id: user.id,
              name: `${user.name} ${user.surname}`,
            },
          });
        }}
      >
        Report
      </Button>
    );
  };
  const CareerSection = () => {
    const t = useTranslations('Profile');

    return (
      <ProfileSection title={t('careerTitle')} className='flex flex-col gap-8'>
        {user.accountType === AccountType.Refugee && (
          <CareerLine
            title={t('previousCareer')}
            type='past'
            industry={user.previousIndustry || undefined}
            jobTitle={user.previousJob}
          />
        )}

        <CareerLine
          title={t('currentCareer')}
          type='current'
          industry={user.currentIndustry || undefined}
          jobTitle={user.currentJob}
        />

        {user.accountType === AccountType.Refugee && (
          <CareerLine
            title={t('futureCareer')}
            type='future'
            industry={user.futureIndustry || undefined}
            jobTitle={user.futureJob}
          />
        )}
      </ProfileSection>
    );
  };
  function ProfileActions({
    user,
    buttonSize = 'large',
  }: {
    user: {
      name?: string | null;
      surname?: string | null;
      accountType?: AccountType | null;
      id: string;
      photo?: string | null;
      idVerificationStatus?: boolean | null;
      helpPreferences?: HelpPreference[] | null;
    };
    buttonSize?: 'large' | 'medium';
  }) {
    const [isMobile] = useIsMobile();

    if (hideActions) {
      return null;
    }
    return (
      <>
        {user.accountType && !thisIsCurrentUserProfile && (
          <ToHelpButton
            userName={`${user.name} ${user.surname}`}
            profilePhoto={user.photo ?? undefined}
            userId={user.id}
            accountType={user.accountType}
            size={buttonSize}
            variant='primary'
            className='w-full'
            onClick={onClose}
            matchId={matchId ?? null}
            idVerificationStatus={user.idVerificationStatus ?? false}
            userHelpPreferences={user.helpPreferences ?? []}
          />
        )}
        <ShareProfileButton
          className='w-full'
          username={`${user.name}`}
          size={buttonSize}
          userId={user.id}
        />
        {!isMobile && <ReportButton />}
      </>
    );
  }

  const MainContent = (
    <>
      <div className='lg:flex lg:gap-4'>
        <Avatar
          imageUrl={user.photo ?? undefined}
          name={`${user.name} ${user.surname}`}
          className='border-white-300 mx-auto mb-10 aspect-square h-[200px] w-[200px] border-8'
        />
        <div className='flex flex-col gap-7 lg:flex-1'>
          <div className='flex w-full items-stretch justify-center gap-5 lg:pt-4'>
            <div className='flex-1 border-r'>
              {user.name || user.surname || adminView ? (
                <h1 className='flex items-center justify-between text-lg font-bold'>
                  <div className='flex flex-row items-center gap-4'>
                    {user.name} {user.surname}{' '}
                    {!user.idVerificationStatus && (
                      <span className='text-destructive-400 flex items-end gap-1 text-xs font-semibold'>
                        <Icon
                          variant={IconName.AlertTriangle}
                          className='bg-destructive-400 translate-y-[-2px] transform'
                        />
                        {t('idNotVerified')}
                      </span>
                    )}
                  </div>
                  {adminView && (
                    <AccountActionsDialog
                      user={{
                        ...user,
                        accountType: user.accountType as AccountType,
                        lastLoginDate: user.lastLoginDate ?? null,
                      }}
                    />
                  )}
                </h1>
              ) : showSkeletonOnEmptyState ? (
                <Skeleton className='h-4 w-[106px]' />
              ) : null}
              <div className='mt-1 flex flex-col gap-2 lg:mt-4'>
                {(user.nationality || showSkeletonOnEmptyState) && (
                  <div className='text-sm capitalize md:flex md:items-center md:gap-2'>
                    {t('From')}:{' '}
                    {user.nationality ? (
                      tNationality(user.nationality)
                    ) : (
                      <Skeleton className='inline-block h-4 w-[190px]' />
                    )}
                    {showSurprisingCountryWarning && (
                      <span className='text-destructive-400 flex items-end gap-1 text-xs font-semibold'>
                        <Icon
                          variant={IconName.AlertTriangle}
                          className='bg-destructive-400 translate-y-[-2px] transform'
                        />
                        {t('idNotVerified')}
                      </span>
                    )}
                  </div>
                )}
                {(user.city || showSkeletonOnEmptyState) && (
                  <div className='flex gap-2 text-sm whitespace-nowrap md:items-center'>
                    {t('resettling', {
                      accountType: user.accountType || AccountType.Supporter,
                      city: user.city || '',
                    })}
                    {!user.city ? (
                      <Skeleton className='inline-block h-4 w-[180px]' />
                    ) : (
                      <LocalTimeDisplay city={user.city} />
                    )}
                  </div>
                )}
                {isMobile && <ReportButton />}
              </div>
              <HelpPreferenceTags
                className='hidden self-start lg:mt-6 lg:flex'
                types={user.helpPreferences as HelpPreference[]}
              />
            </div>
            {user.familyStructure ? (
              <div className='flex flex-col items-center py-1.5 text-center lg:mr-12 lg:w-20'>
                <div
                  className='mb-2 flex max-w-10 flex-1 flex-col items-center justify-center bg-contain bg-center bg-no-repeat lg:max-w-none lg:bg-bottom'
                  style={{
                    backgroundImage: `url(/images/family-structure/${user.familyStructure.toLowerCase()}.svg)`,
                    width:
                      iconDimensions[
                        user.familyStructure as keyof typeof iconDimensions
                      ].width,
                    height:
                      iconDimensions[
                        user.familyStructure as keyof typeof iconDimensions
                      ].height,
                  }}
                ></div>
                <span className='text-xs'>
                  {tFamilyStructure(user.familyStructure)}
                </span>
              </div>
            ) : showSkeletonOnEmptyState ? (
              <Skeleton className='h-[86px] w-[58px]' />
            ) : null}
          </div>
          {user.helpPreferences && user.helpPreferences.length > 0 ? (
            <HelpPreferenceTags
              className='self-start lg:hidden'
              types={user.helpPreferences as HelpPreference[]}
            />
          ) : showSkeletonOnEmptyState ? (
            <div className='flex gap-2'>
              <Skeleton className='h-[26px] w-[100px] rounded-full' />
              <Skeleton className='h-[26px] w-[100px] rounded-full' />
              <Skeleton className='h-[26px] w-[100px] rounded-full' />
            </div>
          ) : null}
          {(user.idVerificationStatus || user.emailVerified) && (
            <div className='flex justify-between gap-2 lg:justify-start'>
              {user.idVerificationStatus && (
                <div className='flex items-center gap-2 text-sm'>
                  <Icon variant={IconName.PatchCheckOn} />
                  <span>{t('idVerified')} </span>
                </div>
              )}
              {user.emailVerified && (
                <div className='flex gap-2 text-sm'>
                  <Icon variant={IconName.PatchCheckOn} />
                  <span>{t('emailVerified')} </span>
                </div>
              )}
            </div>
          )}
          <div className='hidden max-w-[340px] gap-2 lg:flex'>
            <ProfileActions user={user} buttonSize='medium' />
          </div>
        </div>
      </div>
      <div className='mt-12 flex flex-col'>
        {(user.story || showSkeletonOnEmptyState) && (
          <ProfileSection
            title={`${t('storyTitle', { name: user.name ?? '' })}`}
          >
            {user.story ? (
              <TruncateText
                text={user.story}
                maxLength={8}
                className='whitespace-pre-line'
              />
            ) : (
              <div className='flex flex-col gap-2'>
                <Skeleton className='h-4 w-full' />
                <Skeleton className='h-4 w-full' />
                <Skeleton className='h-4 w-1/2' />
              </div>
            )}
          </ProfileSection>
        )}
        {user.availability && user.availability.length > 0 && (
          <ProfileSection title={t('availability')}>
            <span className='bg-white-300 my-8 flex w-fit items-center gap-4 rounded-sm p-2 text-xs'>
              <Icon variant={IconName.Warning} />
              {t('availabilityDescription')}
            </span>
            <AvailabilityViewer
              schedule={user.availability.map(
                (slot) => new TimeSlot(new Date(slot.from), new Date(slot.to))
              )}
              timezone={currentUserTimezone}
              isLoading={isLoadingCurrentUserProfile}
            />
          </ProfileSection>
        )}
        <div className='lg:flex lg:gap-10'>
          {((user.spokenLanguages && user.spokenLanguages.length > 0) ||
            showSkeletonOnEmptyState) && (
            <ProfileSection
              title={`${t('spokenLanguages')}`}
              className='lg:flex-1'
            >
              {user.spokenLanguages && user.spokenLanguages.length > 0 ? (
                <LanguageProficiencyDisplay languages={user.spokenLanguages} />
              ) : (
                <div className='flex flex-col gap-8'>
                  {Array.from({ length: 3 }).map((_, index) => (
                    <div key={index} className='flex flex-col gap-2'>
                      <Skeleton className='h-4 w-1/2' />
                      <Progress value={80} className='w-full' />
                      <Skeleton className='h-3 w-1/4' />
                    </div>
                  ))}
                </div>
              )}
            </ProfileSection>
          )}
          {(user.needsEnglishHelp ||
            user.needsFrenchHelp ||
            showSkeletonOnEmptyState) && (
            <ProfileSection
              title={`${t('languagesHelp', {
                accountType: user.accountType || AccountType.Supporter,
              })}`}
              isBorderLeft={true}
              className='lg:px-10'
            >
              {user.needsEnglishHelp || user.needsFrenchHelp ? (
                <ul className='flex gap-2 text-sm md:flex-col md:gap-6'>
                  {[
                    { language: 'English', needsHelp: user.needsEnglishHelp },
                    { language: 'French', needsHelp: user.needsFrenchHelp },
                  ].map(({ language, needsHelp }) =>
                    needsHelp ? (
                      <li key={language} className='flex items-center gap-2'>
                        <Icon variant={IconName.CheckInCircle} />
                        {language}
                      </li>
                    ) : null
                  )}
                </ul>
              ) : (
                <div className='flex flex-col gap-8'>
                  <Skeleton className='h-4 w-full' />
                  <Skeleton className='h-4 w-full' />
                </div>
              )}
            </ProfileSection>
          )}
        </div>

        {(user.previousIndustry ||
          user.currentIndustry ||
          user.futureIndustry ||
          user.previousJob ||
          user.currentJob ||
          user.futureJob ||
          showSkeletonOnEmptyState) && <CareerSection />}

        {/* TODO: v1 - implement linkedin */}
        {((user.prompts && user.prompts.length > 0) ||
          showSkeletonOnEmptyState) && (
          <ProfileSection title={`${t('Connecting with my Community')}`}>
            <div className='space-y-4'>
              {user.prompts && user.prompts.length > 0
                ? user.prompts.map(({ prompt, answer }, index) => (
                    <div key={prompt} className='flex items-center gap-4'>
                      <span className='text-xs font-semibold'>{index + 1}</span>
                      <Separator orientation='vertical' className='h-12' />
                      <div className='space-y-1'>
                        <p className='text-sm font-semibold'>{prompt}</p>
                        <p>{answer}</p>
                      </div>
                    </div>
                  ))
                : Array.from({ length: 5 }).map((_, index) => (
                    <div key={index} className='flex items-center gap-4'>
                      <span className='text-xs font-semibold'>{index + 1}</span>
                      <Separator orientation='vertical' className='h-12' />
                      <div className='space-y-1'>
                        <Skeleton className='h-4 w-[100px]' />
                        <Skeleton className='h-4 w-[200px]' />
                      </div>
                    </div>
                  ))}
            </div>
          </ProfileSection>
        )}
      </div>
    </>
  );
  const MainContentWrapper = (
    <div className='relative max-w-full flex-1 lg:p-24'>
      <main className='mx-auto flex h-full w-full flex-col p-8 md:h-auto lg:max-w-(--breakpoint-xl) lg:rounded lg:border lg:shadow-xs'>
        {MainContent}
      </main>
      <div className='bg-white-200 sticky right-0 bottom-0 flex gap-4 px-8 py-6 shadow lg:hidden'>
        <ProfileActions user={user} />
      </div>
    </div>
  );

  if (withoutLayout) {
    return MainContent;
  }
  return MainContentWrapper;
}

function ProfileSection({
  title,
  children,
  className,
  isBorderLeft = false,
}: {
  title: string;
  children: React.ReactNode;
  className?: string;
  isBorderLeft?: boolean;
}) {
  const content = (
    <>
      <h2 className='mb-4 font-bold'>{title}</h2>
      {children}
    </>
  );
  return (
    <div className={cn('border-white-400 border-t py-12 lg:py-10', className)}>
      {isBorderLeft ? (
        <div className='lg:h-full lg:border-l lg:pl-10'>{content}</div>
      ) : (
        content
      )}
    </div>
  );
}
